import { useMemo, useState } from 'react';
import { useAuditLog, type AuditItem } from '@/api/hooks';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { PageHeader, Skeleton, EmptyState, Field } from '@/components/primitives';
import { FilterPopover } from '@/components/FilterPopover';
import { cn, etDateTime, etDateTimeFull, timeAgo } from '@/lib/utils';
import { useIsSuperAdmin } from '@/lib/auth';
import { History, AlertCircle, Loader2, ChevronDown, ShieldAlert } from 'lucide-react';

const ACTION_LABELS: Record<string, string> = {
  'template.create': 'Created template',
  'template.update': 'Updated template',
  'template.delete': 'Deleted template',
  'dashboard.create': 'Created dashboard',
  'dashboard.update': 'Updated dashboard',
  'dashboard.delete': 'Deleted dashboard',
  'member.add': 'Added member',
  'member.invite': 'Invited member',
  'member.role': 'Changed member role',
  'member.remove': 'Removed member',
  'user.update': 'Updated user',
  'user.invite': 'Invited user',
  'user.remove': 'Removed user',
  'user.resend': 'Resent invitation',
  'user.invalidate': 'Invalidated sessions',
  'document.download': 'Downloaded document',
  'graphql.mutation': 'Ran GraphQL mutation',
  'auth.login': 'Signed in',
  'auth.loginFailed': 'Failed sign-in',
};

const TARGET_TYPES = ['template', 'dashboard', 'membership', 'user', 'document', 'graphql', 'auth'];

const ACTION_GROUPS = [...new Set(Object.keys(ACTION_LABELS).map((a) => a.split('.')[0]))];

/** Clear-text actor: resolved name/email from the BFF, raw id only as fallback. */
function actorParts(item: AuditItem): { primary: string; secondary?: string } {
  if (item.actorName && item.actorEmail) return { primary: item.actorName, secondary: `(${item.actorEmail})` };
  if (item.actorName) return { primary: item.actorName };
  if (item.actorEmail) return { primary: item.actorEmail };
  if (item.actor) return { primary: item.actor.includes('@') ? item.actor : `${item.actor.slice(0, 8)}…` };
  return { primary: 'unknown' };
}

export function AuditPage() {
  const [actor, setActor] = useState('all');
  const [actionGroup, setActionGroup] = useState('all');
  const [targetType, setTargetType] = useState('all');

  const filters = useMemo(
    () => ({
      ...(actor !== 'all' ? { actor } : {}),
      ...(actionGroup !== 'all' ? { action: `${actionGroup}.` } : {}),
      ...(targetType !== 'all' ? { targetType } : {}),
    }),
    [actor, actionGroup, targetType]
  );

  // The API also enforces this; the guard just avoids a doomed request.
  const isSuperAdmin = useIsSuperAdmin();
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useAuditLog(filters, isSuperAdmin);

  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  // Actors seen in the loaded entries, with their resolved labels (for the filter dropdown)
  const seenActors = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      if (!item.actor || map.has(item.actor)) continue;
      map.set(item.actor, actorParts(item).primary + (actorParts(item).secondary ? ` ${actorParts(item).secondary}` : ''));
    }
    return [...map.entries()];
  }, [items]);

  const activeFilterCount = [actor, actionGroup, targetType].filter((f) => f !== 'all').length;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Audit Log"
        description="Every change made through this admin tool — who, what, when. Times shown in ET (America/New_York)."
        actions={
          <FilterPopover
            activeCount={activeFilterCount}
            onReset={() => {
              setActor('all');
              setActionGroup('all');
              setTargetType('all');
            }}
          >
            <Field label="Actor">
              <Select value={actor} onChange={(e) => setActor(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
                <option value="all">Anyone</option>
                {seenActors.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Action">
              <Select value={actionGroup} onChange={(e) => setActionGroup(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
                <option value="all">Any action</option>
                {ACTION_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Target type">
              <Select value={targetType} onChange={(e) => setTargetType(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
                <option value="all">Any type</option>
                {TARGET_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
          </FilterPopover>
        }
      />

      <Card className="p-0 overflow-hidden">
        {!isSuperAdmin ? (
          <EmptyState
            icon={ShieldAlert}
            title="SuperAdmin access required"
            description="The audit log is only visible to SuperAdmins."
            className="m-4"
          />
        ) : isLoading ? (
          <div className="divide-y divide-border/60">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="px-4 py-3">
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={AlertCircle}
            title="Failed to load the audit log"
            description={error.message}
            className="m-4"
            action={
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={History}
            title="No activity yet"
            description="Actions taken through this tool will appear here, newest first."
            className="m-4"
          />
        ) : (
          <>
            <div className="divide-y divide-border/60">
              {items.map((item) => (
                <AuditRow key={item.id} item={item} />
              ))}
            </div>
            {hasNextPage && (
              <div className="p-3 border-t border-border">
                <Button variant="outline" size="sm" className="w-full" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                  Load older entries
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function AuditRow({ item }: { item: AuditItem }) {
  const [open, setOpen] = useState(false);
  const actor = actorParts(item);
  const detail = item.detail || item.targetId || '';

  return (
    <div className="animate-fade-in">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn('flex w-full items-start gap-4 px-4 py-2.5 text-left text-xs transition-colors hover:bg-muted/40', open && 'bg-muted/40')}
      >
        <span className="shrink-0 w-40 pt-px" title={`${etDateTimeFull(item.ts)}\nUTC: ${item.ts}`}>
          <span className="block text-foreground/90 font-medium tabular-nums">{etDateTime(item.ts)}</span>
          <span className="block text-muted-foreground">{timeAgo(item.ts)}</span>
        </span>
        <span className="min-w-0 flex-1 leading-relaxed">
          <span className="font-medium">{actor.primary}</span>{' '}
          {actor.secondary && (
            <span className="text-muted-foreground" title={item.actor}>
              {actor.secondary}
            </span>
          )}{' '}
          <Badge variant="outline" className="text-[10px] mx-0.5 whitespace-nowrap">
            {ACTION_LABELS[item.action] ?? item.action}
          </Badge>{' '}
          {detail && <span className="text-foreground/85 break-words">{detail}</span>}
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <dl className="grid grid-cols-[7.5rem_1fr] gap-x-4 gap-y-1 border-t border-border/60 bg-muted/25 px-4 py-3 text-[11px] animate-fade-in">
          <dt className="text-muted-foreground">When (ET)</dt>
          <dd className="font-medium tabular-nums">{etDateTimeFull(item.ts)}</dd>
          <dt className="text-muted-foreground">When (UTC)</dt>
          <dd className="font-mono text-[10px]">{item.ts}</dd>
          <dt className="text-muted-foreground">Actor</dt>
          <dd className="break-all">
            {actor.primary}
            {actor.secondary ? ` ${actor.secondary}` : ''}
            {item.actor && <span className="text-muted-foreground"> — {item.actor}</span>}
          </dd>
          <dt className="text-muted-foreground">Action</dt>
          <dd className="font-mono text-[10px]">{item.action}</dd>
          <dt className="text-muted-foreground">Target</dt>
          <dd className="break-all">
            {item.targetType}
            {item.targetId && <span className="text-muted-foreground"> — {item.targetId}</span>}
          </dd>
          {item.detail && (
            <>
              <dt className="text-muted-foreground">Detail</dt>
              <dd className="break-words">{item.detail}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}
